import type { AgreementSigner } from "store/agreementsStore";
import type { TEntity } from "store/beneficiaryStore";
import type { ITerm } from "store/managementStore";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";

export type TFormCreateAgreement = {
    signatory: { firstname: string; id: number | undefined };
    beneficiaryIds?: number[];
};

export type TBeneficiariesInfos = {
    name: string;
    representative?: string;
    entity?: TEntity;
    email?: string;
};

export type TTitleBeneficiaries = {
    options: number[];
    beneficiary: Record<number, TBeneficiariesInfos>;
};

/**
 * Book agreements attach to a work — open the work page.
 * Press / AV still attach to a publication title.
 */
export const getAgreementRelatedResourcePath = (
    portalType: TPortalType,
    ids: { workId?: number | null; titleId?: number | null },
): string | undefined => {
    if (portalType === EPortalType.BOOK) {
        if (ids.workId != null) return `/works/${ids.workId}?filterList=1`;
        if (ids.titleId != null) return `/titles/${ids.titleId}`;
        return undefined;
    }
    if (ids.titleId != null) return `/publications/${ids.titleId}`;
    return undefined;
};

/**
 * Beneficiaries sharing the same work (book) or title (press), for multi-signature.
 */
export const getBeneficiariesOptions = (termAssociatedToTitle: ITerm[]) => {
    return termAssociatedToTitle.reduce(
        (titleBeneficiaries: TTitleBeneficiaries, term) => {
            const hasBeneficiarId = titleBeneficiaries.options.includes(term.beneficiarId);
            if (!hasBeneficiarId) {
                titleBeneficiaries.options.push(term.beneficiarId);
            }
            titleBeneficiaries.beneficiary[term.beneficiarId] = {
                name: term.author || [term.firstname, term.lastname].filter(Boolean).join(" "),
                representative: term.representative,
                entity: term?.entity || "physical",
                email: term.email ?? undefined,
            };
            return titleBeneficiaries;
        },
        {
            options: [],
            beneficiary: {},
        },
    );
};

/**
 * Format signatories data from agreement validation page
 * have the same formatted data as in the agreement validation modal
 * used to successfully show the signature block with all signers
 * We usually got the data from term, but here is from agreement -> signers
 * format the different data ->
 */
type Signer = { name: string };
type SignersDataObj = { [id: number]: Signer };

export const getSignersDataObj = (signers: AgreementSigner[]) => {
    return signers.reduce((acc: SignersDataObj, signer) => {
        acc[signer.relatedResourceId] = {
            name: signer.name,
        };
        return acc;
    }, {});
};
